import { Dialog, IconButton } from "@mui/material"
import { Globe, Home, Mail, Phone, X } from "lucide-mui"
import React from "react"
import ReactMarkdown from "react-markdown"

import BookingPolicyTileIcon from "@/components/booking_policy_tile_icon"
import VenueGoogleMap from "@/components/public/venue/venue_google_map"
import { formatWebsiteLabel } from "@/components/public/venue/venue_info/contact_details"
import VenueOpeningHours from "@/components/public/venue/venue_opening_hours"
import VenueSocialAccounts from "@/components/public/venue/venue_social_accounts"
import type { VenueTagDisplay } from "@/components/venue_tags/types"
import { TAG_CATEGORY_ICONS } from "@/constants/venue_tags"
import i18n from "@/i18n"
import { getBookingPolicyTileTitle, type BookingPolicyTileViewModel } from "@/shared/booking_policy"
import type { OpeningHour } from "@/utils/opening_hours"

const STYLES = {
  topBar: "relative flex shrink-0 items-center justify-center border-b border-[#f1f2f5] bg-white px-5 py-4",
  topBarTitle: "m-0 text-base font-medium leading-4 text-[#19191b]",
  closeButton: "!absolute !right-2 !top-1/2 !-translate-y-1/2",
  scrollArea: "min-h-0 flex-1 overflow-y-auto overscroll-contain",
  section: "px-4 py-6",
  sectionTitle: "mb-4 mt-0 text-base font-medium text-[#222222]",
  divider: "h-2 w-full bg-[#f4f4f4]",
  map: "h-[180px] w-full overflow-hidden",
  row: "flex items-center gap-3 py-[6px]",
  rowIcon: "h-4 w-4 shrink-0 text-[#9ca3af]",
  rowLabel: "w-[104px] shrink-0 text-[13px] leading-5 text-[#9ca3af]",
  rowValue: "min-w-0 flex-1 break-words text-[13px] leading-5 text-[#19191b]",
  rowLink: "text-[13px] leading-5 text-[#19191b] no-underline hover:underline",
  socials: "mt-5",
  policyPanel: "mt-4 rounded-[10px] bg-[#fafafa] px-5 py-5",
  policyMarkdown:
    "prose prose-sm max-w-none text-[12px] leading-[18px] text-[#5f6774] prose-p:my-0 prose-ul:my-0 prose-li:my-0 prose-strong:text-[#444] prose-strong:font-semibold",
} as const

const DIALOG_SX = {
  "& .MuiDialog-container": {
    alignItems: "flex-end",
  },
} as const

const PAPER_PROPS = {
  sx: {
    width: "100%",
    margin: 0,
    borderTopLeftRadius: "10px",
    borderTopRightRadius: "10px",
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    height: "calc(100dvh - 44px)",
    maxHeight: "calc(100dvh - 44px)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
} as const

const InfoRow: React.FC<{
  icon: React.ReactNode
  label: string
  testId: string
  children: React.ReactNode
}> = ({ icon, label, testId, children }) => (
  <div className={STYLES.row} data-testid={testId}>
    {icon}
    <span className={STYLES.rowLabel}>{label}</span>
    {children}
  </div>
)

export type VenueInfoModalProps = {
  open: boolean
  onClose: () => void
  venueName: string
  address?: string | null
  zipcode?: string | null
  city?: string | null
  country?: string | null
  phone?: string | null
  email?: string | null
  website?: string | null
  latitude?: number | null
  longitude?: number | null
  socialAccounts?: Record<string, string> | null
  highlights: VenueTagDisplay[]
  openingHours: OpeningHour[]
  timezone: string
  bookingPolicyTiles: BookingPolicyTileViewModel[]
  bookingPolicyContent?: string | null
}

const VenueInfoModal: React.FC<VenueInfoModalProps> = ({
  open,
  onClose,
  venueName,
  address,
  zipcode,
  city,
  country,
  phone,
  email,
  website,
  latitude,
  longitude,
  socialAccounts,
  highlights,
  openingHours,
  timezone,
  bookingPolicyTiles,
  bookingPolicyContent,
}) => {
  const cityLine = [zipcode, city].filter(Boolean).join(" ")
  const fullAddress = [address, cityLine, country].filter(Boolean).join(", ")
  const hasCoordinates = latitude != null && longitude != null
  const hasSocialAccounts = socialAccounts && Object.keys(socialAccounts).length > 0
  const hasBookingPolicy = bookingPolicyTiles.length > 0 || Boolean(bookingPolicyContent?.trim())

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      data-testid="public-venue-info-modal"
      sx={DIALOG_SX}
      PaperProps={PAPER_PROPS}
    >
      <div className={STYLES.topBar} data-testid="public-venue-info-modal-top-bar">
        <h2 className={STYLES.topBarTitle}>{i18n.t("app.public.venue.info_modal.title")}</h2>
        <IconButton
          onClick={onClose}
          className={STYLES.closeButton}
          aria-label={i18n.t("app.public.venue.info_modal.close")}
          data-testid="public-venue-info-modal-close"
        >
          <X className="h-5 w-5 text-[#19191b]" />
        </IconButton>
      </div>

      <div className={STYLES.scrollArea} data-testid="public-venue-info-modal-content">
        {!!hasCoordinates && (
          <div className={STYLES.map} data-testid="public-venue-info-modal-map">
            <VenueGoogleMap latitude={latitude} longitude={longitude} venueName={venueName} />
          </div>
        )}

        <div className={STYLES.section}>
          {!!fullAddress && (
            <InfoRow
              icon={<Home className={STYLES.rowIcon} />}
              label={i18n.t("app.public.venue.info_modal.address")}
              testId="public-venue-info-modal-address"
            >
              <span className={STYLES.rowValue}>{fullAddress}</span>
            </InfoRow>
          )}
          {!!phone && (
            <InfoRow
              icon={<Phone className={STYLES.rowIcon} />}
              label={i18n.t("app.public.venue.info_modal.telephone")}
              testId="public-venue-info-modal-phone"
            >
              <a href={`tel:${phone}`} className={STYLES.rowLink}>
                {phone}
              </a>
            </InfoRow>
          )}
          {!!email && (
            <InfoRow
              icon={<Mail className={STYLES.rowIcon} />}
              label={i18n.t("app.public.venue.info_modal.email")}
              testId="public-venue-info-modal-email"
            >
              <a href={`mailto:${email}`} className={STYLES.rowLink}>
                {email}
              </a>
            </InfoRow>
          )}
          {!!website && (
            <InfoRow
              icon={<Globe className={STYLES.rowIcon} />}
              label={i18n.t("app.public.venue.info_modal.webpage")}
              testId="public-venue-info-modal-website"
            >
              <a href={website} target="_blank" rel="noopener noreferrer" className={STYLES.rowLink}>
                {formatWebsiteLabel(website)}
              </a>
            </InfoRow>
          )}
          {!!hasSocialAccounts && (
            <div className={STYLES.socials}>
              <VenueSocialAccounts socialAccounts={socialAccounts} />
            </div>
          )}
        </div>

        {highlights.length > 0 && (
          <>
            <div className={STYLES.divider} />
            <div className={STYLES.section} data-testid="public-venue-info-modal-highlights">
              <h2 className={STYLES.sectionTitle}>{i18n.t("app.public.venue.info_modal.highlights")}</h2>
              {highlights.map((highlight) => {
                const Icon = TAG_CATEGORY_ICONS[highlight.category]
                return (
                  <InfoRow
                    key={highlight.category}
                    icon={<Icon className={STYLES.rowIcon} />}
                    label={highlight.label}
                    testId={`public-venue-info-modal-highlight-${highlight.category}`}
                  >
                    <span className={STYLES.rowValue}>{highlight.values}</span>
                  </InfoRow>
                )
              })}
            </div>
          </>
        )}

        <div className={STYLES.divider} />
        <div className={STYLES.section}>
          <VenueOpeningHours openingHours={openingHours} timezone={timezone} />
        </div>

        {!!hasBookingPolicy && (
          <>
            <div className={STYLES.divider} />
            <div className={STYLES.section} data-testid="public-venue-info-modal-booking-policy">
              <h2 className={STYLES.sectionTitle}>{i18n.t("app.booking_policy.title")}</h2>
              {bookingPolicyTiles.map((tile) => (
                <InfoRow
                  key={tile.kind}
                  icon={<BookingPolicyTileIcon kind={tile.kind} className={STYLES.rowIcon} />}
                  label={getBookingPolicyTileTitle(tile.kind)}
                  testId={`public-venue-info-modal-policy-${tile.kind}`}
                >
                  <span className={STYLES.rowValue}>{tile.subtitle}</span>
                </InfoRow>
              ))}
              {!!bookingPolicyContent?.trim() && (
                <div className={STYLES.policyPanel} data-testid="public-venue-info-modal-policy-content">
                  <div className={STYLES.policyMarkdown}>
                    <ReactMarkdown>{bookingPolicyContent}</ReactMarkdown>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Dialog>
  )
}

export default VenueInfoModal
